import React, { useEffect, useState } from 'react';
import {
  Box, VStack, HStack, Heading, Text, Button, Table, Thead, Tbody, Tr, Th, Td,
  useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalCloseButton, ModalFooter, FormControl, FormLabel, Input, useToast,
  Badge, Spinner, Icon,
} from '@chakra-ui/react';
import { AddIcon, TimeIcon } from '@chakra-ui/icons';
import { departmentApi } from '../services/api';
import { DepartmentWithStats, Target, Risk } from '../types';
import { useLanguage } from '../hooks/useLanguage';
import { getRiskLevelStyle, getRiskLevelLabel } from '../utils/riskLevel';

const DepartmentManagement: React.FC = () => {
  const [departments, setDepartments] = useState<DepartmentWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: _onDetailClose } = useDisclosure();
  const { t } = useLanguage();

  const handleCloseDetail = () => {
    setSelectedDept(null);
    setDeptTargets([]);
    setDeptRisks([]);
    setDeptActivity([]);
    _onDetailClose();
  };
  const [editItem, setEditItem] = useState<DepartmentWithStats | null>(null);
  const [formData, setFormData] = useState({ unit_id: '', department_name: '', work_unit: '' });
  const [selectedDept, setSelectedDept] = useState<DepartmentWithStats | null>(null);
  const [deptTargets, setDeptTargets] = useState<Target[]>([]);
  const [deptRisks, setDeptRisks] = useState<Risk[]>([]);
  const [deptActivity, setDeptActivity] = useState<any[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const toast = useToast();

  const fetchData = async () => {
    try {
      const response = await departmentApi.getAll();
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = () => {
    setEditItem(null);
    setFormData({ unit_id: '', department_name: '', work_unit: '' });
    onOpen();
  };

  const handleEdit = (item: DepartmentWithStats) => {
    setEditItem(item);
    setFormData({ unit_id: item.unit_id, department_name: item.department_name, work_unit: item.work_unit || '' });
    onOpen();
  };

  const handleSave = async () => {
    try {
      if (editItem) {
        await departmentApi.update(editItem.unit_id, { department_name: formData.department_name, work_unit: formData.work_unit || null });
        toast({ title: t('dept.updatedSuccess'), status: 'success' });
      } else {
        await departmentApi.create(formData);
        toast({ title: t('dept.createdSuccess'), status: 'success' });
      }
      fetchData();
      onClose();
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('dept.saveError'), status: 'error' });
    }
  };

  const handleDelete = async (unitId: string) => {
    if (!window.confirm(t('dept.confirmDelete'))) return;
    try {
      await departmentApi.delete(unitId);
      toast({ title: t('dept.deletedSuccess'), status: 'success' });
      fetchData();
    } catch (error) {
      toast({ title: t('dept.deleteError'), status: 'error' });
    }
  };

  const handleViewDetail = async (dept: DepartmentWithStats) => {
    setSelectedDept(dept);
    setDetailLoading(true);
    onDetailOpen();
    try {
      const [targetsRes, risksRes, activityRes] = await Promise.allSettled([
        departmentApi.getTargets(dept.unit_id),
        departmentApi.getRisks(dept.unit_id),
        departmentApi.getActivity(dept.unit_id),
      ]);
      setDeptTargets(targetsRes.status === 'fulfilled' ? targetsRes.value.data : []);
      setDeptRisks(risksRes.status === 'fulfilled' ? risksRes.value.data : []);
      setDeptActivity(activityRes.status === 'fulfilled' ? activityRes.value.data : []);
    } catch (error) {
      console.error('Error fetching department details:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading) return <Spinner size="xl" display="block" mx="auto" mt={10} />;

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        <HStack justifyContent="space-between">
          <Heading size="lg" color="blue.600">{t('dept.title')}</Heading>
          <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={handleCreate}>{t('dept.add')}</Button>
        </HStack>

        <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
          <Table variant="simple" size="sm">
            <Thead bg="#0F2C59">
              <Tr>
                <Th color="white">{t('dept.unitId')}</Th>
                <Th color="white">{t('dept.departmentName')}</Th>
                <Th color="white">{t('dept.workUnit')}</Th>
                <Th color="white" isNumeric>{t('dept.targets')}</Th>
                <Th color="white" isNumeric>{t('dept.risks')}</Th>
                <Th color="white">{t('common.actions')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {departments.map((item) => (
                <Tr key={item.unit_id} _hover={{ bg: 'gray.50' }}>
                  <Td fontWeight="bold">{item.unit_id}</Td>
                  <Td>{item.department_name}</Td>
                  <Td>{item.work_unit || '-'}</Td>
                  <Td isNumeric>
                    <Badge colorScheme="blue" borderRadius="full">{item.target_count}</Badge>
                  </Td>
                  <Td isNumeric>
                    <Badge colorScheme={item.risk_count > 0 ? 'red' : 'gray'} borderRadius="full">{item.risk_count}</Badge>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <Button size="sm" colorScheme="teal" variant="ghost" onClick={() => handleViewDetail(item)}>{t('dept.view')}</Button>
                      <Button size="sm" colorScheme="yellow" variant="ghost" onClick={() => handleEdit(item)}>{t('common.edit')}</Button>
                      <Button size="sm" colorScheme="red" variant="ghost" onClick={() => handleDelete(item.unit_id)}>{t('common.delete')}</Button>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </VStack>

      {/* Create/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editItem ? t('dept.editTitle') : t('dept.addTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              {!editItem && (
                <FormControl isRequired>
                  <FormLabel>{t('dept.unitId')}</FormLabel>
                  <Input value={formData.unit_id} onChange={(e) => setFormData({ ...formData, unit_id: e.target.value })} placeholder={t('dept.unitIdPlaceholder')} />
                </FormControl>
              )}
              <FormControl isRequired>
                <FormLabel>{t('dept.departmentName')}</FormLabel>
                <Input value={formData.department_name} onChange={(e) => setFormData({ ...formData, department_name: e.target.value })} placeholder={t('dept.namePlaceholder')} />
              </FormControl>
              <FormControl>
                <FormLabel>{t('dept.workUnit')}</FormLabel>
                <Input value={formData.work_unit} onChange={(e) => setFormData({ ...formData, work_unit: e.target.value })} placeholder={t('dept.workUnitPlaceholder')} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>{t('common.cancel')}</Button>
            <Button colorScheme="blue" onClick={handleSave}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Detail Modal */}
      <Modal isOpen={isDetailOpen} onClose={handleCloseDetail} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{selectedDept?.department_name} ({selectedDept?.unit_id})</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {detailLoading ? (
              <Spinner display="block" mx="auto" />
            ) : (
              <VStack spacing={6} align="stretch">
                {/* Activity Log */}
                <Box>
                  <HStack mb={3}>
                    <Icon as={TimeIcon} color="blue.500" />
                    <Heading size="sm" color="blue.600">{t('dept.recentActivity', { n: deptActivity.length })}</Heading>
                  </HStack>
                  {deptActivity.length === 0 ? (
                    <Text color="gray.500" fontSize="sm" fontStyle="italic">{t('dept.noActivity')}</Text>
                  ) : (
                    <Box maxH="300px" overflowY="auto" border="1px" borderColor="gray.200" borderRadius="md">
                      {deptActivity.map((act, idx) => (
                        <HStack
                          key={idx}
                          px={4} py={2}
                          borderBottom="1px" borderColor="gray.100"
                          _hover={{ bg: 'gray.50' }}
                          spacing={3}
                        >
                          <Badge
                            colorScheme={act.action === 'สร้าง' ? 'green' : 'yellow'}
                            minW="50px" textAlign="center"
                          >
                            {act.action}
                          </Badge>
                          <Badge colorScheme="gray" minW="40px" textAlign="center">
                            {act.entity_type}
                          </Badge>
                          <Text fontSize="sm" flex="1" noOfLines={1}>{act.entity_name}</Text>
                          <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
                            {new Date(act.timestamp).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })}
                          </Text>
                        </HStack>
                      ))}
                    </Box>
                  )}
                </Box>

                <Box>
                  <Heading size="sm" mb={2} color="blue.600">{t('dept.relatedTargets', { n: deptTargets.length })}</Heading>
                  {deptTargets.length === 0 ? (
                    <Text color="gray.500" fontSize="sm">{t('dept.noTargets')}</Text>
                  ) : (
                    <Table size="sm" variant="simple">
                      <Thead bg="gray.100">
                        <Tr>
                          <Th>{t('dept.targetId')}</Th>
                          <Th isNumeric>{t('dept.targetNo')}</Th>
                          <Th>{t('dept.targetDescription')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {deptTargets.map((tgt) => (
                          <Tr key={tgt.target_id}>
                            <Td fontWeight="bold">{tgt.target_id}</Td>
                            <Td isNumeric>{tgt.target_no}</Td>
                            <Td>{tgt.target_description}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  )}
                </Box>

                <Box>
                  <Heading size="sm" mb={2} color="red.600">{t('dept.relatedRisks', { n: deptRisks.length })}</Heading>
                  {deptRisks.length === 0 ? (
                    <Text color="gray.500" fontSize="sm">{t('dept.noRisks')}</Text>
                  ) : (
                    <Table size="sm" variant="simple">
                      <Thead bg="gray.100">
                        <Tr>
                          <Th>{t('common.riskCode')}</Th>
                          <Th>{t('dept.riskName')}</Th>
                          <Th>{t('dept.riskType')}</Th>
                          <Th isNumeric>{t('common.inherent')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {deptRisks.map((r) => {
                          const level = getRiskLevelStyle(r.inherent_likelihood, r.inherent_impact);
                          return (
                            <Tr key={r.risk_id}>
                              <Td fontWeight="bold">{r.risk_code}</Td>
                              <Td maxW="200px" whiteSpace="normal">{r.risk_name}</Td>
                              <Td>{r.risk_type?.name_th || '-'}</Td>
                              <Td fontWeight="bold" bg={level.bg} color={level.text} textAlign="center">{getRiskLevelLabel(r.inherent_likelihood, r.inherent_impact)}</Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  )}
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={handleCloseDetail}>{t('common.close')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DepartmentManagement;
